import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, Check, ArrowUpRight, ShoppingBag } from "lucide-react";
import { PILLARS, SERVICES } from "../mock";
import { useCart } from "../context/CartContext";

export default function BuildPage() {
  const [params, setParams] = useSearchParams();
  const initialPillar = params.get("pillar") || "nutrition";
  const [activePillar, setActivePillar] = useState(initialPillar);
  const { items, add, total, count, setOpen } = useCart();

  useEffect(() => {
    if (initialPillar !== activePillar) setActivePillar(initialPillar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPillar]);

  const onSelectPillar = (id) => {
    setActivePillar(id);
    setParams({ pillar: id });
  };

  const services = useMemo(
    () => SERVICES.filter((s) => s.pillarId === activePillar),
    [activePillar]
  );
  const pillar = PILLARS.find((p) => p.id === activePillar);
  const inCart = (id) => items.some((i) => i.id === id);

  return (
    <>
      {/* Header / hero */}
      <section className="pt-[120px] lg:pt-[150px] pb-10 bg-[var(--lc-cream)]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow text-[var(--lc-moss)] mb-5">Build Your Plan</div>
              <h1 className="font-serif-display text-[clamp(40px,6vw,84px)] leading-[1] font-light">
                Mix services across <span className="italic">six pillars.</span>
              </h1>
              <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-[var(--lc-muted)]">
                Add individual services to your selection. We'll review your
                package and send a personalized quote within 24 hours.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <div className="inline-flex items-center gap-3 bg-white border border-[var(--lc-line)] rounded-md px-4 py-3">
                <ShoppingBag className="h-4 w-4 text-[var(--lc-moss)]" />
                <div className="text-sm">
                  <span className="font-semibold">{count}</span>
                  <span className="text-[var(--lc-muted)]"> service{count === 1 ? "" : "s"} selected</span>
                </div>
                <button
                  onClick={() => setOpen(true)}
                  className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--lc-moss)] hover:underline"
                >
                  Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar selector */}
      <section className="sticky top-[68px] lg:top-[76px] z-30 bg-[var(--lc-cream)] border-y border-[var(--lc-line)]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-3">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              const active = activePillar === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => onSelectPillar(p.id)}
                  className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12px] font-bold tracking-[0.16em] uppercase whitespace-nowrap transition-colors ${
                    active
                      ? "bg-[var(--lc-ink)] text-[var(--lc-cream)]"
                      : "bg-white text-[var(--lc-ink)] border border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {p.short}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-[var(--lc-cream)] py-12 lg:py-16">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Pillar card */}
            <div className="lg:col-span-4">
              <div className="sticky top-[150px]">
                <div className="relative aspect-[4/5] rounded-md overflow-hidden">
                  <img src={pillar.image} alt={pillar.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="eyebrow text-[var(--lc-sand)] mb-2">Pillar {pillar.number}</div>
                    <div className="font-serif-display text-3xl leading-tight">{pillar.name}</div>
                    <p className="mt-3 text-[13px] text-white/80 leading-relaxed">{pillar.description}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Services */}
            <div className="lg:col-span-8">
              <div className="flex items-center justify-between mb-6">
                <div className="font-serif-display text-2xl">Services <span className="text-[var(--lc-muted)] text-base align-middle">({services.length})</span></div>
                <div className="text-xs text-[var(--lc-muted)]">Contact to discuss pricing</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 lg:gap-5">
                {services.map((svc) => {
                  const added = inCart(svc.id);
                  return (
                    <article
                      key={svc.id}
                      className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6 flex flex-col hover:border-[var(--lc-moss)] hover:shadow-md transition-all"
                    >
                      <h4 className="font-serif-display text-lg lg:text-xl leading-tight">{svc.name}</h4>
                      <p className="mt-2 text-[13px] leading-relaxed text-[var(--lc-muted)] flex-1">{svc.description}</p>
                      <div className="mt-5 pt-4 border-t border-[var(--lc-line)] flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-[var(--lc-ink)]">Contact to discuss</div>
                          <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--lc-muted)]">per {svc.unit}</div>
                        </div>
                        <button
                          onClick={() =>
                            add({ ...svc, pillarName: pillar.short })
                          }
                          className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase transition-colors ${
                            added
                              ? "bg-[var(--lc-moss)] text-white"
                              : "bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)]"
                          }`}
                        >
                          {added ? <><Check className="h-3.5 w-3.5" /> Added</> : <><Plus className="h-3.5 w-3.5" /> Add</>}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Floating CTA */}
              {count > 0 && (
                <div className="mt-12 bg-[var(--lc-ink)] text-[var(--lc-cream)] rounded-md p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="eyebrow text-[var(--lc-sand)] mb-2">Your Custom Package</div>
                    <div className="font-serif-display text-2xl lg:text-3xl">{count} service{count === 1 ? "" : "s"} selected</div>
                    <div className="text-xs text-white/70 mt-1">Tailored quote sent within 24 hours</div>
                  </div>
                  <Link
                    to="/checkout"
                    className="group inline-flex items-center justify-center gap-2 px-6 py-4 bg-[var(--lc-moss)] hover:bg-[var(--lc-sand)] hover:text-[var(--lc-ink)] text-white text-[12px] font-bold tracking-[0.18em] uppercase transition-colors"
                  >
                    Request Quote <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
